import React, { useCallback, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import scrollIntoView from 'scroll-into-view-if-needed';
import styled from 'styled-components';

import { useCustomer, useMembership } from '@techstyle/react-accounts';
import { useFeature } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';
import { updateSessionDetail, useSession } from '@techstyle/redux-core';

import { toggleCartDrawer } from '../../actions/cart';
import {
  addBasketItem,
  addMembershipToCart,
  loadCart,
} from '../../actions/checkout';
import {
  trackClickSizeSelection,
  trackMembershipCta,
} from '../../actions/products';
import {
  AddToBagErrorCode,
  MembershipActionLocation,
  MembershipEventAction,
} from '../../constants/checkout';
import { useBraSizeSelectionsInit } from '../../slices/braSizeSelections';
import { desktop, mobile } from '../../styles';
import { Paragraph } from '../../styles/paragraph';
import formatProductUrl from '../../utils/formatProductUrl';
import { localStorage } from '../../utils/helpers';
import isNewPricingPromoBannersFlagOn from '../../utils/isNewPricingPromoBannersFlagOn';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  findBraSizeCategory,
  findMatchingSize,
  getDefaultColor,
  getDefaultSelectedPrice,
  getDefaultSingleSelections,
  getDefaultSize,
  getLinkedProductsSizeList,
  getProductCategoryKey,
  getProductCategoryTypes,
} from '../../utils/productDetail';
import {
  getAvailableQuantity,
  getAvailableQuantitySize,
} from '../../utils/productQuantity';
import {
  allowExclusiveCheckout as allowExclusiveCheckoutSelector,
  getByoSetsByProduct,
  getInfluencerItemCountExcludingMembership,
  getMembershipCartItem,
  getMinimumQuantity,
  getProductPromos,
  getShouldShowPromoPrice,
  isByoAvailableItem as isByoAvailableItemSelector,
  isComingSoonProduct as isComingSoonProductSelector,
  isExclusiveProduct,
  isHiddenFromGrid as isHiddenFromGridSelector,
  isNMP,
  isPreorderable as isPreorderableSelector,
  isSoldOut as isSoldOutSelector,
  isXtraVipBox as isXtraVipBoxSelector,
  shouldHideRelatedProducts as shouldHideRelatedProductsSelector,
} from '../../utils/selectors';
import { hasBraSize, isOneSize } from '../../utils/sizes';
import usePrevious from '../../utils/usePrevious';
import { Component as AddedToBasketCountFlag } from '../AddedToBasketCountFlag';
import { Component as ByoNoCredits } from '../ByoNoCredits';
import { useByoContext } from '../ByoProvider';
import { Component as ColorSwatch } from '../ColorSwatch';
import { Container as PreorderShipDate } from '../PreorderShipDate';
import { Component as ProductDetailByoOptions } from '../ProductDetailByoOptions';
import { Component as ProductDetailColorSwatches } from '../ProductDetailColorSwatches';
import { Component as ProductDetailCTAs } from '../ProductDetailCTAs';
import { Container as ProductDetailSize } from '../ProductDetailSize';
import { Component as SportPdpIndicator } from '../SportPdpIndicator';

const Wrapper = styled.div`
  ${mobile`
    padding-left: ${props => props.theme.spacing.small}px;
    padding-right: ${props => props.theme.spacing.small}px;
  `}
`;

const SingleProductColorSwatches = styled(ProductDetailColorSwatches)`
  & ${ColorSwatch.ColorTitle} {
  }

  ${desktop`
    margin-bottom: ${props => props.theme.spacing.small}px;
  `};
`;

const StyledPreorderShipDate = styled(PreorderShipDate)`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.3px;
  margin: 23px 0 28px;
`;

const BorderfreeUnavailable = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.3px;
  margin: 23px 0 28px;
`;

const NoPromoInfluencer = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.medium}px;
  margin-bottom: ${props => props.theme.spacing.small}px;
  border-bottom-left-radius: ${props => props.theme.spacing.tiny}px;
  border-bottom-right-radius: ${props => props.theme.spacing.tiny}px;
  border: solid 1px ${props => props.theme.colors.lavender250};
`;

const NoPromoInfluencerTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const NoPromoInfluencerText = styled(Paragraph)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.33;
  letter-spacing: 0.3px;
  text-align: center;
`;

const TotalItem = styled.span`
  font-weight: bold;
`;

export default function ProductSingleDetailOptions({
  children,
  className,
  handleHideRegCta,
  handleSizeChange: handleSizeChangeFromProps,
  hideRegCta,
  product,
  renderAddToBagError,
  showToastMessage,
  shouldProductFitReviewAlignToRight,
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const byoContext = useByoContext();
  const { isVip, networkStatus } = useMembership();
  const { dmGatewayCode: dmgCode, isServerOnly } = useSession();
  const profile = useCustomer();
  const {
    'added-to-basket-flag': addedToBasketFlagEnabled,
    'new-product-pricing': shouldDisplayNewProductPricing,
    'experiment-new-pricing-promo-banners': newPricingPromoBannersFlag,
  } = useLDFlags();
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();

  const { psrc } = router?.query ?? {};
  const displayNewProductPricing =
    // exclude strings starting with 'sale' for NewProductPricing Experiment
    !/^sale/.test(psrc) &&
    (isNewPricingPromoBannersFlagOn(newPricingPromoBannersFlag)
      ? newPricingPromoBannersFlag.newPricing
      : shouldDisplayNewProductPricing);

  const {
    allowExclusiveCheckout,
    availableByoSetsByProduct,
    cartItemInfluencerCount,
    isByoAvailableItem,
    isComingSoonProduct,
    isExclusive,
    isHiddenFromGrid,
    isPreorderable,
    isSoldOut,
    isXtraVipBox,
    membershipItem,
    minimumQuantity,
    promos,
    shouldHideRelatedProducts,
    shouldShowPromoPrice,
    userIsNMP,
  } = useSelector(state => {
    return {
      allowExclusiveCheckout: allowExclusiveCheckoutSelector(state),
      availableByoSetsByProduct: getByoSetsByProduct(state, { product }),
      cartItemInfluencerCount: getInfluencerItemCountExcludingMembership(state),
      isByoAvailableItem: isByoAvailableItemSelector(state, { product }),
      isComingSoonProduct: isComingSoonProductSelector(state, { product }),
      isExclusive: isExclusiveProduct(state, { product }),
      isHiddenFromGrid: isHiddenFromGridSelector(state, { product }),
      isPreorderable: isPreorderableSelector(state, { product }),
      isSoldOut: isSoldOutSelector(state, { product }),
      isXtraVipBox: isXtraVipBoxSelector(state, { product }),
      membershipItem: getMembershipCartItem(state),
      minimumQuantity: getMinimumQuantity(state, { product }).minimumQuantity,
      promos: getProductPromos(state, { product }),
      shouldHideRelatedProducts: shouldHideRelatedProductsSelector(state, {
        product,
      }),
      shouldShowPromoPrice: getShouldShowPromoPrice(state, { product }),
      userIsNMP: isNMP(state),
    };
  });
  const { isEnabled: shouldOnlyShowSportsForVIP } = useFeature('sport_unlock');
  const { 'token-to-cash': tokenToCashEnabled } = useLDFlags();

  const { isBorderfreeCustomer } = useSelector(state => state.borderfree);
  const cart = useSelector(state => state.checkout);
  const { deselectedMembershipCheckout } = cart;
  const { allowOutOfStockCheckout } = useSelector(state => state.debug);

  const membershipResolved = !isServerOnly && networkStatus.isUpToDate;

  const sizeCache = JSON.parse(localStorage.getItem('selectedSize'));
  let sizeRef = new Map();
  const { influencerInfo, influencerPromoInfo, isInfluencer } = profile;

  const {
    product_category_id_list: productCategoryIdList,
    tag_id_list: tagIdList,
  } = product;

  const { isSport } = getProductCategoryTypes(productCategoryIdList, tagIdList);

  /// //state//////
  const [cartButtonDisabled, setCartButtonDisabled] = useState(false);
  const [currentSize, setCurrentSize] = useState(null);
  const [displayBraSize, setDisplayBraSize] = useState(hasBraSize(product));
  // Track whether the user has changed the price so that if the default
  // selection changes, we know whether to update it or not.
  const [hasChangedPrice] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(
    getDefaultSelectedPrice({ isVip, dmgCode, isBorderfreeCustomer })
  );
  const defaultSelectedSize = useMemo(
    () =>
      getDefaultSingleSelections(
        product,
        profile,
        isSoldOut,
        sizeCache,
        isPreorderable
      ),
    [product, profile, isSoldOut, sizeCache, isPreorderable]
  );
  const [selections, setSelections] =
    useBraSizeSelectionsInit(defaultSelectedSize);
  const [selectedProductQuantity, setselectedProductQuantity] =
    useState(minimumQuantity);
  // This will be used for the ProductDetailColorSwatches in order
  // to update only if the size is completed
  const [selectedSizeValue, setSelectedSizeValue] = useState(
    defaultSelectedSize.size
  );
  /// //end state/////

  const {
    isUserByoEligible,
    isFlowInitiated,
    isUserEmpWithoutCredits,
    clickedFromByoGrids,
  } = byoContext;

  const shouldShowByoCTA = tokenToCashEnabled
    ? isUserByoEligible &&
      isByoAvailableItem &&
      availableByoSetsByProduct.length &&
      !userIsNMP
    : isUserByoEligible &&
      isByoAvailableItem &&
      availableByoSetsByProduct.length;
  const shouldHideAddToBagCTA = isFlowInitiated && clickedFromByoGrids;

  const hasNoInfluencerPromo =
    isInfluencer && !influencerPromoInfo.userHasActivePromo;

  const isSourceBuildYourOwn =
    psrc === 'Build Your Own' || psrc === 'browse_build_your_own';
  const shouldShowByoNoCredits =
    isVip &&
    isUserEmpWithoutCredits &&
    (isByoAvailableItem || isSourceBuildYourOwn);
  const previousSelections = usePrevious(selections);

  useEffect(() => {
    const productType = getProductCategoryKey(productCategoryIdList, tagIdList);
    const isCategoryProfileExist = profile && profile[productType];
    if (!isCategoryProfileExist) {
      dispatch(
        updateSessionDetail({
          name: 'cache_size_eligibility',
          value: '',
        })
      );
    }
  }, [dispatch, product, productCategoryIdList, profile, tagIdList]);

  // this is needed because the membershipItem was not being correctly loaded
  useEffect(() => {
    if (!membershipItem) {
      dispatch(loadCart());
    }
  }, [dispatch, membershipItem]);

  // This is used for ProductDetailColorSwatches that
  // it will update only if the size is completed
  useEffect(() => {
    if (previousSelections !== selections && selections.size.value !== null) {
      setSelectedSizeValue(selections.size);
    }
  }, [selections, previousSelections]);

  // updateOnProductChange
  useEffect(() => {
    // Clear out state if navigating from product set to single product (or vice versa)
    // Find matching size if changing colors, and if size not available in new color then clear out the size

    let newSelections = {};
    let band = null;
    let cup = null;

    const { master_product_id: masterProductId } = product;
    const nextColor = getDefaultColor(product);

    // FIXME: `currentSize` should be referenced from `prevState`, not `this.state` (above)
    // Destructuring from `this.state` to stop the no-access-state-in-setstate lint rule.
    // It SHOULD be fine using `prevState` but this change was made before a code freeze
    // and we did not have time to test it to make sure it didn't break anything
    // major.
    // This also goes for `displayBraSize` below
    const prevSize = currentSize || selections.size;

    /** if we are navigating to a color where size isn't available then we clear the size out **/
    if (
      prevSize.value != null &&
      getAvailableQuantitySize(nextColor.sizes, prevSize, isPreorderable) <=
        0 &&
      !isOneSize(product)
    ) {
      newSelections = {
        masterProductId,
        color: nextColor,
        size: {
          sizeProduct: null,
          value: null,
          error: null,
          band,
          cup,
        },
      };
    } else {
      const nextSize = findMatchingSize(
        product,
        prevSize,
        nextColor.sizes,
        profile,
        isPreorderable
      );
      const size = nextSize || getDefaultSize(product, profile);

      if (displayBraSize && size.value != null) {
        const braSizeCategorized = findBraSizeCategory(size.value);
        band = braSizeCategorized.band;
        cup = braSizeCategorized.cup;
      }

      newSelections = {
        masterProductId,
        color: nextColor,
        size: {
          ...size,
          band,
          cup,
        },
      };
    }

    setSelections(newSelections);
    setDisplayBraSize(hasBraSize(product));
    setSelectedSizeValue(newSelections.size);

    // Reset the sizeRef map
    sizeRef = new Map();
  }, [product.permalink]);

  // updateOnProfileChange
  const previousProfile = usePrevious(profile);
  useEffect(() => {
    if (profile && previousProfile && profile !== previousProfile) {
      let newSelections = {};

      const size = selections.size;

      const newSize =
        size.sizeProduct === null ? getDefaultSize(product, profile) : size;

      newSelections = { ...selections, size: newSize };

      setSelections(newSelections);
    }
  }, [profile, product, selections, previousProfile, setSelections]);

  useEffect(() => {
    if (!hasChangedPrice) {
      setSelectedPrice(
        getDefaultSelectedPrice({ isVip, dmgCode, isBorderfreeCustomer })
      );
    }
  }, [dmgCode, hasChangedPrice, isBorderfreeCustomer, isVip]);

  useEffect(() => {
    setDisplayBraSize(hasBraSize(product));
  }, [product, product.default_product_category_id]);

  const updateSizeRef = id => node => {
    sizeRef.set(id, node);
  };

  const scrollToSelectSize = id => {
    const node = sizeRef.get(id);

    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        scrollMode: 'always',
        block: 'center',
      });
    }
  };

  const checkInfluencerLimit = useCallback(() => {
    const influencerLimit = influencerInfo.maxProductQty;

    if (cartItemInfluencerCount + 1 > influencerLimit) {
      return true;
    } else {
      return false;
    }
  }, [cartItemInfluencerCount, influencerInfo]);

  const checkSizes = () => {
    let updatedSelections = selections;
    const itemsWithErrors = [];
    const productItem = selections;

    const { size } = productItem;
    let updatedItem = productItem;

    const isMissingSize = !size.value || !size.sizeProduct;

    let error = null;

    if (isMissingSize) {
      error = 'SIZE_REQUIRED';
    } else {
      const availableQuantity = isSoldOut
        ? 0
        : getAvailableQuantity(size.sizeProduct, product, isPreorderable);

      const isSizeSoldOut = size.sizeProduct
        ? availableQuantity <= 0
        : isSoldOut;

      if (isSizeSoldOut) {
        error = 'SIZE_UNAVAILABLE';
      }
    }

    if (error !== size.error) {
      updatedItem = {
        ...updatedItem,
        size: { ...size, error },
      };

      updatedSelections = {
        ...updatedItem,
      };
    }

    if (updatedItem.size.error) {
      itemsWithErrors.push(updatedItem);
    }

    if (updatedSelections !== selections) {
      setSelections(updatedSelections);
    }

    return itemsWithErrors;
  };

  // Add products to cart button
  const addToCart = async option => {
    setCartButtonDisabled(true);

    // Open Cart Drawer immediately and show loading state in Drawer
    dispatch(toggleCartDrawer(true));
    dispatch(updateSessionDetail({ name: 'sxf_minicart', value: '' }));

    // we can put out of stock item in the cart - temporary decision

    const tasks = [];

    const pSource = router.query.psrc;
    const productToAdd = selections.size.sizeProduct;

    const shouldAddMembership =
      (option === 'vip' && !isVip && !isInfluencer && !membershipItem) ||
      (selectedPrice === 'defaultPrice' &&
        !isVip &&
        !isInfluencer &&
        !membershipItem &&
        !deselectedMembershipCheckout &&
        option !== 'guest');

    // find the product based on the selectedSize.value
    tasks.push(() =>
      dispatch(
        addBasketItem(
          productToAdd.product_id,
          selectedProductQuantity,
          {
            location: MembershipActionLocation.PDP,
          },
          pSource
        )
      )
    );

    if (shouldAddMembership) {
      tasks.push(() =>
        dispatch(
          addMembershipToCart({
            location: MembershipActionLocation.PDP,
            eventAction: MembershipEventAction.AUTO_ADD,
          })
        )
      );
    }

    // Run tasks serially so that parallel responses don't overwrite each other
    // in a nondeterministic way.
    while (tasks.length) {
      const task = tasks.shift();
      try {
        await task();
        if (shoppingBagFlagStatus?.isControl) {
          showToastMessage(
            <FormattedMessage
              id="global_checkout.cart_item_added_success_message"
              defaultMessage="Item added"
            />
          );
        }
      } catch (err) {
        if ((err && err.errorCode) || (err && err.message)) {
          if (err && err.message && err.message.includes('No inventory')) {
            renderAddToBagError(AddToBagErrorCode.OUT_OF_STOCK);
            dispatch(toggleCartDrawer(false));
          } else {
            renderAddToBagError(err.errorCode);
          }
          Sentry.captureException(err);
        }
        if (shoppingBagFlagStatus?.isVariant) {
          const toastMessage = (
            <FormattedMessage
              id="global_checkout.something_went_wrong"
              defaultMessage="Something went wrong. Please try again."
            />
          );
          showToastMessage(toastMessage, 6000, true);
        }
      }
    }

    setCartButtonDisabled(false);
    if (shouldAddMembership) {
      handleHideRegCta(false);
    }
  };

  const handleInfluencerError = () => {
    const influencerLimit = influencerInfo.maxProductQty;
    const total = `${influencerLimit}/${influencerLimit}`;
    showToastMessage(
      <FormattedMessage
        id="site_pdp.influencer_limit"
        defaultMessage="You've got {total} items in your cart. Check out now."
        values={{
          total: <TotalItem>{total}</TotalItem>,
        }}
      />
    );
  };

  const handleItemErrors = itemsWithErrors => {
    const firstItem = itemsWithErrors[0];
    scrollToSelectSize(firstItem.masterProductId);
    if (firstItem.size.error === 'SIZE_REQUIRED') {
      showToastMessage(
        <FormattedMessage
          id="site_pdp.size_required_error_message"
          defaultMessage="Select a size"
        />
      );
    } else if (firstItem.size.error === 'SIZE_UNAVAILABLE') {
      showToastMessage(
        <FormattedMessage
          id="site_pdp.size_sold_out_error_message"
          defaultMessage="Your selected size is sold out. Select another size."
        />
      );
    }
  };

  const handleRegCtaAddToCart = () => {
    handleAddToCart('');
    dispatch(
      updateSessionDetail({
        name: 'add_to_cart_count',
        value: '',
      })
    );
  };

  const handleAddToCart = option => {
    // Check for errors first before adding to cart
    let hasReachLimit = false;

    if (isInfluencer) {
      hasReachLimit = checkInfluencerLimit();
    }
    if (hasReachLimit) {
      handleInfluencerError();
    } else {
      const itemsWithErrors = checkSizes();
      if (itemsWithErrors.length) {
        handleItemErrors(itemsWithErrors);
      } else {
        addToCart(option);
      }
    }
  };

  const handleColorChange = (event, color, id) => {
    const pSource = router.query.psrc;
    // Carry out the original psrc
    const { as, href } = formatProductUrl({
      extraQueryParams: { psrc: pSource },
      permalink: color.permalink,
      productId: color.value,
    });
    router.replace(href, as);
  };

  const handleQuantityChange = newQuantity => {
    if (newQuantity < minimumQuantity) {
      return;
    }
    setselectedProductQuantity(newQuantity);
  };

  const handleToggleButtonClick = option => {
    handleAddToCart(option);
    dispatch(
      updateSessionDetail({
        name: 'add_to_cart_count',
        value: option,
      })
    );
    dispatch(trackMembershipCta(option));
  };

  const setSizeCache = (size, productType) => {
    const selectedSizeCache =
      JSON.parse(localStorage.getItem('selectedSize')) || {};
    selectedSizeCache[productType] = size;
    localStorage.setItem('selectedSize', JSON.stringify(selectedSizeCache));
  };

  const handleSizeChange = (option, id, field) => {
    const { psrc } = router.query;
    let band = null;
    let cup = null;

    dispatch(
      trackClickSizeSelection({
        productID: id,
        isSet: false,
        prodSize: option.id,
      })
    );

    // if we set up band only
    if (field === 'bandSize') {
      setSelections(selections => {
        return {
          ...selections,
          size: {
            sizeProduct: null,
            value: null,
            error: null,
            band: option,
            cup,
          },
        };
      });
      return;
      // if we set up cup only
    } else if (field === 'cupSize') {
      setSelections(selections => {
        return {
          ...selections,
          size: {
            sizeProduct: null,
            value: null,
            error: null,
            band,
            cup: option,
          },
        };
      });
      return;
    }
    const currentSelection = selections;

    // Derive `sizeProduct` from `value`
    const sizeList = currentSelection.color.sizes;
    const newSizeProduct = sizeList.find(size =>
      option.isOneSize
        ? size.product_id === option.productId
        : size.size === option.value
    );
    // The redirect should occur for "OSFM" products when there is more than one
    // "OSFM" available.
    const isOneSize = option.isOneSize;
    const hasMultipleOneSize = sizeList?.length > 1 && isOneSize;

    const productType = getProductCategoryKey(productCategoryIdList, tagIdList);
    if (option.value) {
      setSizeCache(option.value.toString(), productType);
    }

    if (field === 'braSize') {
      // both band and cup are selected but combination exist or not
      const categorizeBraSize = newSizeProduct
        ? findBraSizeCategory(newSizeProduct.size)
        : findBraSizeCategory(option);
      band = categorizeBraSize.band;
      cup = categorizeBraSize.cup;
    }

    if (option.productId && (option.isOneSize ? hasMultipleOneSize : true)) {
      // Take the user to the Sister SKU.
      const { permalink, productId } = option;
      // Use product type to set whether 'sets' is true
      const productType = '';
      // Need to use the old-style URL because we only have the ID, no other
      // product data.
      const newSize = {
        sizeProduct: newSizeProduct,
        value: option.value,
        error:
          option.isSoldOut && !allowOutOfStockCheckout
            ? 'SIZE_UNAVAILABLE'
            : null,
      };

      setCurrentSize(newSize);
      setselectedProductQuantity(1);

      const { as, href } = formatProductUrl({
        extraQueryParams: { psrc, isClickFromPdp: true },
        permalink,
        productId,
        productType,
      });
      router.replace(href, as);

      if (handleSizeChangeFromProps) {
        handleSizeChangeFromProps(productId, newSize);
      }
    } else if (!newSizeProduct) {
      // Reset to default
      setSelections(selections => {
        return {
          ...selections,
          size: {
            sizeProduct: null,
            value: option != null ? option : null,
            error:
              option.value === '' || option === '' ? null : 'SIZE_UNAVAILABLE',
            band: band != null ? band : null,
            cup: cup != null ? cup : null,
          },
        };
      });
    } else {
      const newSize = {
        sizeProduct: newSizeProduct,
        value: option.value,
        error:
          option.isSoldOut && !allowOutOfStockCheckout
            ? 'SIZE_UNAVAILABLE'
            : null,
        band,
        cup,
      };

      setCurrentSize(newSize);
      setCartButtonDisabled(false);
      // Reset product quantity to 1 after product size change
      setselectedProductQuantity(1);
      setSelections(selections => {
        return {
          ...selections,
          size: newSize,
        };
      });
    }
  };

  const renderSingleProduct = () => {
    const { isFlowInitiated } = byoContext;
    const id = product.master_product_id;
    const currentSelection = selections;
    const selectedColor = currentSelection.color.value;
    const selectedColorLabel = currentSelection.color.name;
    const selectedSize = currentSelection.size;
    const sizeList = getLinkedProductsSizeList(product);

    return (
      <>
        {/* Sending first product and first selected size to generate the color swatches. Assuming all products and all sizes have the same number of color options. */}
        <SingleProductColorSwatches
          className={className}
          id={id}
          isBorderfreeCustomer={isBorderfreeCustomer}
          isSoldOut={isSoldOut}
          limit={10}
          onChangeColor={handleColorChange}
          masterProduct={product}
          selectedColor={selectedColor}
          selectedColorLabel={selectedColorLabel}
          selectedSize={selectedSizeValue}
          showSelectedOnly={shouldHideRelatedProducts}
        />
        <ProductDetailSize
          disabled={isXtraVipBox && isSoldOut}
          displayBraSize={displayBraSize}
          error={selectedSize.error}
          id={id}
          innerRef={updateSizeRef(id)}
          isSoldOut={isSoldOut}
          hideQuantity={isFlowInitiated}
          onChangeQuantity={handleQuantityChange}
          onChangeSize={handleSizeChange}
          masterProduct={product}
          minimumQuantity={minimumQuantity}
          selectedColorLabel={selectedColorLabel}
          selectedProductQuantity={selectedProductQuantity}
          shouldProductFitReviewAlignToRight={
            shouldProductFitReviewAlignToRight
          }
          shouldSplitCombinedSize
          size={selectedSize}
          sizeList={sizeList}
        />
        {children}
      </>
    );
  };

  if (!product) {
    return null;
  }

  const isBorderfreeUnavailable = isBorderfreeCustomer && isPreorderable;
  const shouldNotShowCtaForSports = isSport && !shouldOnlyShowSportsForVIP;

  return (
    <Wrapper className={className}>
      {renderSingleProduct()}
      {addedToBasketFlagEnabled ? (
        <AddedToBasketCountFlag product={product} />
      ) : null}
      {isBorderfreeUnavailable ? (
        <BorderfreeUnavailable>
          <FormattedMessage
            id="site_pdp.border_free_pdp_unavailable"
            defaultMessage="Item not available in your area"
          />
        </BorderfreeUnavailable>
      ) : (
        <StyledPreorderShipDate product={product} />
      )}
      {shouldNotShowCtaForSports ? <SportPdpIndicator /> : null}
      {shouldHideAddToBagCTA ? null : (
        <ProductDetailCTAs
          allowExclusiveCheckout={allowExclusiveCheckout}
          cart={cart}
          constructorButtonLabel="add_to_bag"
          handleRegCtaAddToCart={handleRegCtaAddToCart}
          handleToggleButtonClick={handleToggleButtonClick}
          hideRegCta={hideRegCta}
          isCartButtonDisabled={cartButtonDisabled}
          isComingSoonProduct={isComingSoonProduct}
          isExclusive={isExclusive}
          isHiddenFromGrid={isHiddenFromGrid}
          isSoldOut={isSoldOut}
          isXtraVipBox={isXtraVipBox}
          isPreorderable={isPreorderable}
          isProductSet={false}
          membershipResolved={membershipResolved}
          membershipItem={membershipItem}
          product={product}
          promos={promos}
          selections={selections}
          shouldNotShowCtaForSports={shouldNotShowCtaForSports}
          shouldShowPromoPrice={shouldShowPromoPrice}
          showToastMessage={showToastMessage}
          shouldShowNewProductPricing={displayNewProductPricing}
        />
      )}
      {Boolean(shouldShowByoCTA) && !shouldNotShowCtaForSports ? (
        <ProductDetailByoOptions
          checkSizes={() => checkSizes()}
          handleItemErrors={handleItemErrors}
          product={product}
          sizeProduct={selections.size.sizeProduct}
          selectedProductQuantity={selectedProductQuantity}
        />
      ) : null}

      {shouldShowByoNoCredits ? <ByoNoCredits /> : null}

      {hasNoInfluencerPromo && (
        <NoPromoInfluencer>
          <NoPromoInfluencerTitle>
            <FormattedMessage
              id="site_pdp.no_promo_influencer_title"
              defaultMessage="No Promo Available"
            />
          </NoPromoInfluencerTitle>
          <NoPromoInfluencerText>
            <FormattedMessage
              id="site_pdp.no_promo_influencer_text"
              defaultMessage="Sorry! You don’t have an active promo code, and this is your ambassador account. This item cannot be added to bag. Need some Savage new items? Email us: ambassadors@savagex.com"
            />
          </NoPromoInfluencerText>
        </NoPromoInfluencer>
      )}
    </Wrapper>
  );
}

ProductSingleDetailOptions.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  handleHideRegCta: PropTypes.func,
  handleSizeChange: PropTypes.func,
  hideRegCta: PropTypes.bool,
  product: PropTypes.object,
  renderAddToBagError: PropTypes.func,
  shouldProductFitReviewAlignToRight: PropTypes.bool,
  showToastMessage: PropTypes.func.isRequired,
};
